import * as Yup from 'yup';

import {
    Brand,
    Product,
    ProductAttribute,
    ProductLine,
} from '@barboxx/models/types';
import {
    FLAVOR_TYPES,
    MEDIUM_TEXT,
    MINIMUM_TEXT,
    PRODUCT_ATTRIBUTE_TYPES,
    TINY_TEXT,
} from '@barboxx/types';
import { enumToList, transformEmptyToNull } from '@barboxx/utils';

export const BRAND_SCHEMA = Yup.object<Brand.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string()
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(TINY_TEXT, 'Too Long!')
        .required(),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    manufacturer: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    countryOfOrigin: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    website: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
});
export const CREATE_BRAND_SCHEMA = BRAND_SCHEMA.omit(['id']);

export const PRODUCT_LINE_SCHEMA = Yup.object<ProductLine.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string()
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(TINY_TEXT, 'Too Long!')
        .required(),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null)
        .min(MINIMUM_TEXT, 'Too Short!')
        .max(MEDIUM_TEXT, 'Too Long!'),
    brandId: Yup.number().required('Required'),
});
export const CREATE_PRODUCT_LINE_SCHEMA = PRODUCT_LINE_SCHEMA.omit(['id']);

export const PRODUCT_SCHEMA = Yup.object<Product.Base>().shape({
    id: Yup.number().required(),
    name: Yup.string().required('Required'),
    brandId: Yup.number().required('Required'),
    isAlcoholic: Yup.bool().default(true),
    flavor: Yup.string()
        .oneOf(enumToList(FLAVOR_TYPES))
        .nullable()
        .defined()
        .default(null),

    isGroupingOnly: Yup.bool().default(false),
    productTypeId: Yup.number()
        .nullable()
        .default(null)
        .when('isAlcoholic', {
            is: (value: boolean) => value === true,
            then: schema => schema.required('Required'),
        }),
    productSubtypeId: Yup.number().nullable().default(null),
    productLineId: Yup.number().nullable().default(null),
    description: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
    abvPercentage: Yup.number()
        .nullable()
        .default(null)
        .when('isAlcoholic', {
            is: (value: boolean) => value === true,
            then: schema =>
                schema
                    .required('Required')
                    .max(100, 'Must be 0-100%')
                    .min(0, 'Must be 0-100%'),
        }),
    baseProductId: Yup.number()
        .nullable()
        .default(null)
        .when('isVariant', {
            is: (value: boolean) => value === true,
            then: schema => schema.required('Required'),
        }),
    volumeMl: Yup.number().nullable().default(null),
    image: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
});
export const CREATE_PRODUCT_SCHEMA = PRODUCT_SCHEMA.omit(['id']);

export const PRODUCT_ATTRIBUTE_SCHEMA =
    Yup.object<ProductAttribute.Base>().shape({
        id: Yup.number().required(),
        name: Yup.string().required('Required'),
        description: Yup.string()
            .nullable()
            .transform(transformEmptyToNull)
            .default(null),
        attributeType: Yup.string()
            .oneOf(enumToList(PRODUCT_ATTRIBUTE_TYPES))
            .required(),
    });
export const CREATE_PRODUCT_ATTRIBUTE_SCHEMA = PRODUCT_ATTRIBUTE_SCHEMA.omit([
    'id',
]);
